import { create } from 'zustand';
import type { PeerStats, CallSummary } from '@/hooks/useWebRtc';

// Welle 6: NFC-Karten-Daten vom Lese-Bridge (Spec: lokaler-windows-client-api-referenz)
export interface NfcReadPayload {
  success: boolean;
  pan?: string;
  bancName?: string;
  blz?: string;
  bic?: string;
  expirationDate?: string;
  chipCardNumber?: string;
  bancLocation?: string;
  accountNumber?: string;
}

/**
 * Welle 8c-pre38: Eintrag der Session-Ablage. Echte Inhalte (kein Mockup mehr):
 * signierte PDFs, Scans/Snapshots und je gelesener EC-Karte ein Protokoll-Eintrag.
 */
export interface SessionDoc {
  id: string;
  kind: 'signed' | 'scan' | 'nfc';
  name: string;
  status: string;        // anzeigbarer Status inkl. Uhrzeit
  at: number;
  url?: string;          // Blob-URL (signiertes PDF / Scan)
  data?: Record<string, string>; // NFC-Felder fürs Protokoll
}

export type ViewName =
  | 'login'
  | 'dashboard'
  | 'incoming'
  | 'incall'
  | 'incall-doc-focus'
  | 'incall-inspection'
  | 'incall-translation'
  | 'incall-recording'
  | 'incall-turn-warn'
  | 'links'
  | 'wrapup'
  | 'wrap';

export type StatusKind = 'available' | 'pause' | 'wrap' | 'phone';

// pre170: einzeln abschaltbare Erreichbarkeits-Mechanismen (Default: alle an).
export type ReachPrefKey = 'notifications' | 'serviceWorker' | 'webPush' | 'wakeLock' | 'pwaInstalled' | 'audioHeartbeat';

export interface AudioPrefs {
  ringtone: boolean;
  accept: boolean;
  hangup: boolean;
  snap: boolean;
  ringtoneId: string;
}

export interface UIState {
  view: ViewName;
  status: StatusKind;
  phoneBusy: boolean;   // pre140: Telefonie belegt (true = im Telefonat)
  phoneSource: 'auto' | 'manual' | null; // pre151: wie 'phone' gesetzt wurde
  phoneConfigured: boolean;              // pre151: Telefonie-Schnittstelle aktiv?
  companionConnected: boolean;           // pre154: Companion-Sitzung verbunden?
  theme: 'dark' | 'light';

  // In-Call sub-states
  docFocused: boolean;
  inspectionOn: boolean;
  translationOn: boolean;
  captionExpanded: boolean;
  recordingOn: boolean;
  showTurnWarn: boolean;

  // Header notification chips
  nfcChipVisible: boolean;
  networkChipVisible: boolean;

  // Welle 6: NFC-Daten vom lokalen Stele-Client
  nfcData: NfcReadPayload | null;
  /** Welle 9: Anzeigeinfos des aktiven/eingehenden Calls (Stele-Name, Standort) für die UI. */
  incomingCall: { steleName?: string; steleLocation?: string | null; tenantName?: string; group?: string } | null;
  nfcReceivedAt: number | null;

  // Welle 8c-pre38: Session-Ablage (echte Inhalte)
  sessionDocs: SessionDoc[];

  // Welle 8c-pre31/32: Live-Verbindungsstatistik beider Seiten + RTT-Historie JE Seite
  // für die Kachel (zwei Graphen). lastCallSummary trägt die Qualitätszusammenfassung
  // des zuletzt beendeten Gesprächs in die Wrap-Ansicht.
  callStatsAgent: PeerStats | null;
  callStatsStele: PeerStats | null;
  rttHistoryAgent: number[];
  rttHistoryStele: number[];
  lastCallSummary: CallSummary | null;

  // Toolbar state
  micMuted: boolean;
  camOn: boolean;
  shareOn: boolean;

  // Side panel section state (collapsed flag)
  sectionsCollapsed: Record<string, boolean>;

  // Overlays
  cmdkOpen: boolean;
  helpOpen: boolean;
  helpSlug: string | null;
  onboardingOpen: boolean;
  cmdkQuery: string;
  settingsOpen: boolean;
  settingsTab: 'general' | 'devices' | 'storage' | 'reach';
  reachPopoverOpen: boolean;
  reachPrefs: Record<ReachPrefKey, boolean>;
  pwaToastVisible: boolean;
  escalateOpen: boolean;
  transferOpen: boolean;

  // Audio preferences
  audioPrefs: AudioPrefs;

  // WebRTC
  rtcToken: string | null;
  activeCallId: string | null;

  // Caption display
  capSizeCustomer: number;
  capSizeSelf: number;
  capWidthPercent: number;

  // Actions
  setView: (view: ViewName) => void;
  setStatus: (status: StatusKind) => void;
  setPhoneBusy: (busy: boolean) => void;
  applyPhoneSignal: (busy: boolean) => void;   // pre151: Schnittstellen-Signal → Anzeige
  setPhoneConfigured: (configured: boolean) => void;
  setCompanionConnected: (connected: boolean) => void;
  setTheme: (theme: 'dark' | 'light') => void;
  toggleSection: (id: string) => void;
  toggleMic: () => void;
  toggleCam: () => void;
  toggleShare: () => void;
  toggleInspection: () => void;
  toggleTranslation: () => void;
  toggleCaptionExpanded: () => void;
  toggleRecording: () => void;
  setNfcChipVisible: (v: boolean) => void;
  setNfcData: (data: NfcReadPayload | null) => void;
  setIncomingCall: (info: UIState["incomingCall"]) => void;
  clearNfcData: () => void;
  addSessionDoc: (doc: Omit<SessionDoc, 'id' | 'at'> & { id?: string }) => void;
  clearSessionDocs: () => void;
  setCallStatsAgent: (s: PeerStats | null) => void;
  setCallStatsStele: (s: PeerStats | null) => void;
  pushRttAgent: (ms: number) => void;
  pushRttStele: (ms: number) => void;
  setCallSummary: (s: CallSummary | null) => void;
  resetCallStats: () => void;
  setNetworkChipVisible: (v: boolean) => void;
  showHeaderChip: (chip: 'nfc' | 'network', durationMs?: number) => void;
  setCmdkQuery: (q: string) => void;
  openCmdK: () => void;
  closeCmdK: () => void;
  openHelp: (slug?: string) => void;
  closeHelp: () => void;
  openOnboarding: () => void;
  closeOnboarding: () => void;
  toggleSettings: () => void;
  openSettings: (tab?: 'general' | 'devices' | 'storage' | 'reach') => void;
  setSettingsTab: (tab: 'general' | 'devices' | 'storage' | 'reach') => void;
  toggleReachPopover: () => void;
  setReachPref: (key: ReachPrefKey, on: boolean) => void;
  showPwaToast: () => void;
  hidePwaToast: () => void;
  openEscalate: () => void;
  openTransfer: () => void;
  closeDrawers: () => void;
  setAudioPref: (key: keyof AudioPrefs, value: boolean) => void;
  setRingtone: (id: string) => void;
  setCapSize: (side: 'customer' | 'self', px: number) => void;
  setCapWidth: (percent: number) => void;
  swapCameras: () => void;
}

export const useUIStore = create<UIState>((set, get) => ({
  view: 'dashboard',
  status: 'available',
  phoneBusy: false,
  phoneSource: null,
  phoneConfigured: false,
  companionConnected: false,
  theme: 'light',

  docFocused: false,
  inspectionOn: false,
  translationOn: false,
  captionExpanded: false,
  recordingOn: false,
  showTurnWarn: false,

  nfcChipVisible: false,
  networkChipVisible: false,
  nfcData: null,
  incomingCall: null,
  nfcReceivedAt: null,
  sessionDocs: [],
  callStatsAgent: null,
  callStatsStele: null,
  rttHistoryAgent: [],
  rttHistoryStele: [],
  lastCallSummary: null,

  micMuted: false,
  camOn: true,
  shareOn: false,

  sectionsCollapsed: {
    'sec-customer': false,
    'sec-telemetry': true, // default collapsed laut User-Wunsch
    'sec-translation': false,
    'sec-docs': false,
  },

  cmdkOpen: false,
  helpOpen: false,
  helpSlug: null,
  onboardingOpen: false,
  cmdkQuery: '',
  settingsOpen: false,
  settingsTab: 'devices',
  reachPopoverOpen: false,
  reachPrefs: { notifications: true, serviceWorker: true, webPush: true, wakeLock: true, pwaInstalled: true, audioHeartbeat: true },
  pwaToastVisible: false,
  escalateOpen: false,
  transferOpen: false,

  audioPrefs: {
    ringtone: true,
    accept: true,
    hangup: true,
    snap: true,
    ringtoneId: 'office-chime',
  },

  rtcToken: null,
  activeCallId: null,

  capSizeCustomer: 24,
  capSizeSelf: 15,
  capWidthPercent: 64,

  setView: (view) => {
    const s = get();
    // Reset transient In-Call state when leaving call
    const next: Partial<UIState> = { view };
    if (!view.startsWith('incall')) {
      next.translationOn = false;
      next.inspectionOn = false;
      next.captionExpanded = false;
      next.networkChipVisible = false;
      next.showTurnWarn = false;
    }
    if (view === 'incall-translation') {
      next.translationOn = true;
      next.docFocused = false;
    }
    if (view === 'incall-doc-focus') next.docFocused = true;
    if (view === 'incall' || view === 'incall-recording') next.docFocused = false;
    if (view === 'incall-inspection') next.inspectionOn = true;
    if (view === 'incall-recording') next.recordingOn = true;
    if (view === 'incall-turn-warn') {
      next.showTurnWarn = true;
      next.networkChipVisible = true;
    }
    set(next);
  },

  setStatus: (status) => set({ status, phoneSource: status === 'phone' ? 'manual' : null }),
  setPhoneBusy: (phoneBusy) => set({ phoneBusy }),
  setPhoneConfigured: (phoneConfigured) => set({ phoneConfigured }),
  setCompanionConnected: (companionConnected) => set({ companionConnected }),
  // pre151: Schnittstellen-Signal (Webex/Teams) auf die Anzeige abbilden.
  // Automatik greift nur, wenn der Nutzer 'verfügbar' ist bzw. zuvor automatisch
  // auf 'phone' gesetzt wurde — eine manuelle Wahl (pause/wrap/verfügbar) gewinnt.
  applyPhoneSignal: (busy) => set((st) => {
    if (busy) {
      if (st.status === 'available') return { phoneBusy: true, status: 'phone', phoneSource: 'auto' };
      return { phoneBusy: true };
    }
    if (st.status === 'phone' && st.phoneSource === 'auto') {
      return { phoneBusy: false, status: 'available', phoneSource: null };
    }
    return { phoneBusy: false };
  }),
  setTheme: (theme) => {
    document.documentElement.setAttribute('data-theme', theme);
    set({ theme });
  },

  toggleSection: (id) =>
    set((s) => ({
      sectionsCollapsed: { ...s.sectionsCollapsed, [id]: !s.sectionsCollapsed[id] },
    })),

  toggleMic: () => set((s) => ({ micMuted: !s.micMuted })),
  toggleCam: () => set((s) => ({ camOn: !s.camOn })),
  toggleShare: () => set((s) => ({ shareOn: !s.shareOn })),
  toggleInspection: () => set((s) => ({ inspectionOn: !s.inspectionOn })),
  toggleTranslation: () =>
    set((s) => ({
      translationOn: !s.translationOn,
      captionExpanded: s.translationOn ? false : s.captionExpanded,
    })),
  toggleCaptionExpanded: () => set((s) => ({ captionExpanded: !s.captionExpanded })),
  toggleRecording: () => set((s) => ({ recordingOn: !s.recordingOn })),

  setNfcChipVisible: (v) => set({ nfcChipVisible: v }),
  setNfcData: (data) => set({ nfcData: data, nfcReceivedAt: data ? Date.now() : null }),
  setIncomingCall: (info) => set({ incomingCall: info }),
  clearNfcData: () => set({ nfcData: null, nfcReceivedAt: null }),
  addSessionDoc: (doc) =>
    set((st) => ({
      sessionDocs: [
        ...st.sessionDocs,
        { id: doc.id ?? `doc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, at: Date.now(), ...doc },
      ],
    })),
  clearSessionDocs: () => set({ sessionDocs: [] }),
  setCallStatsAgent: (s) => set({ callStatsAgent: s }),
  setCallStatsStele: (s) => set({ callStatsStele: s }),
  pushRttAgent: (ms) => set((st) => ({ rttHistoryAgent: [...st.rttHistoryAgent.slice(-39), ms] })),
  pushRttStele: (ms) => set((st) => ({ rttHistoryStele: [...st.rttHistoryStele.slice(-39), ms] })),
  setCallSummary: (s) => set({ lastCallSummary: s }),
  // lastCallSummary wird hier NICHT gelöscht: die Zusammenfassung des gerade beendeten
  // Calls soll in der Wrap-Ansicht verfügbar bleiben. Sie wird beim Start des nächsten
  // Calls (InCall-Mount) zurückgesetzt.
  resetCallStats: () => set({ callStatsAgent: null, callStatsStele: null, rttHistoryAgent: [], rttHistoryStele: [] }),
  setNetworkChipVisible: (v) => set({ networkChipVisible: v }),
  showHeaderChip: (chip, durationMs) => {
    if (chip === 'nfc') set({ nfcChipVisible: true });
    if (chip === 'network') set({ networkChipVisible: true });
    if (durationMs) {
      setTimeout(() => {
        if (chip === 'nfc') set({ nfcChipVisible: false });
        if (chip === 'network') set({ networkChipVisible: false });
      }, durationMs);
    }
  },

  openCmdK: () => set({ cmdkOpen: true }),
  setCmdkQuery: (cmdkQuery) => set({ cmdkQuery }),
  closeCmdK: () => set({ cmdkOpen: false, cmdkQuery: '' }),
  openHelp: (slug) => set({ helpOpen: true, helpSlug: slug ?? null, cmdkOpen: false, cmdkQuery: '' }),
  closeHelp: () => set({ helpOpen: false, helpSlug: null }),
  openOnboarding: () => set({ onboardingOpen: true }),
  closeOnboarding: () => set({ onboardingOpen: false }),
  toggleSettings: () => set((s) => ({ settingsOpen: !s.settingsOpen })),
  openSettings: (tab) => set((s) => ({ settingsOpen: true, settingsTab: tab ?? s.settingsTab })),
  setSettingsTab: (settingsTab) => set({ settingsTab }),
  toggleReachPopover: () => set((s) => ({ reachPopoverOpen: !s.reachPopoverOpen })),
  setReachPref: (key, on) => set((s) => ({ reachPrefs: { ...s.reachPrefs, [key]: on } })),
  showPwaToast: () => set({ pwaToastVisible: true }),
  hidePwaToast: () => set({ pwaToastVisible: false }),
  openEscalate: () => set({ escalateOpen: true, transferOpen: false }),
  openTransfer: () => set({ transferOpen: true, escalateOpen: false }),
  closeDrawers: () => set({ escalateOpen: false, transferOpen: false }),

  setAudioPref: (key, value) =>
    set((s) => ({ audioPrefs: { ...s.audioPrefs, [key]: value } })),

  setRingtone: (id) => set((s) => ({ audioPrefs: { ...s.audioPrefs, ringtoneId: id } })),

  setCapSize: (side, px) => {
    document.documentElement.style.setProperty(`--cap-size-${side}`, `${px}px`);
    set(side === 'customer' ? { capSizeCustomer: px } : { capSizeSelf: px });
  },
  setCapWidth: (percent) => {
    document.documentElement.style.setProperty('--cap-width', `${percent}%`);
    set({ capWidthPercent: percent });
  },

  swapCameras: () => set((s) => ({ docFocused: !s.docFocused })),
}));
